/* ============================================================================
   McDuffy Quiz v2 — DATA LAYER
   ----------------------------------------------------------------------------
   Pure data + copy. No DOM, no state. Browser global `window.MQ_DATA` AND a
   CommonJS export (so the engine can be unit-tested in node).

   IP NOTE: every string here is original McDuffy copy. Zero tails/FD assets,
   icons, fonts, illustrations, or microcopy. ₱ currency. Banned words
   (cheap/budget/basic/affordable/value/lower tier) never appear — there is a
   dev-time assertion for this in the engine test.
   ============================================================================ */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.MQ_DATA = api;
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  /* Words copy must NEVER use (CLAUDE.md). Used by the dev lint test. */
  var BANNED_WORDS = ['cheap', 'budget', 'basic', 'affordable', 'value', 'lower tier'];

  /* --- Recipes (unified subscription price, September 2026) ---------------
     Allergens drive exclusion; chicken_rice is the fish-free default lead.
     `ingredients` (display string), `kcalPerKg` (lab-derived Modified Atwater
     display figure where available; null where no display
     figure is published) and `nutrition` (1-line benefit) feed the recipe-picker
     info panels + the reveal-screen ingredients line. Portion math remains the
     server-owned feeding-guide table, not a browser kcal calculation. */
  var RECIPES = {
    chicken_rice: {
      key: 'chicken_rice', label: 'Chicken It Up',
      pricePerBagPhp: 259, allergens: ['chicken', 'pork', 'grains'], /* contains rice */
      fishFree: true,
      tagline: 'No fish — great for sensitive dogs.',
      benefits: ['Gently cooked for easy digestion', 'Single familiar protein', 'Wholesome rice for steady energy'],
      ingredients: 'Chicken, pork, rice, spinach, sweet potato, moringa, fish oil, vitamins & minerals',
      kcalPerKg: 1372,
      nutrition: 'Gentle, no-fish recipe for sensitive dogs.'
    },
    beef_rice: {
      key: 'beef_rice', label: 'Braised Beef Bowl',
      pricePerBagPhp: 259, allergens: ['beef', 'grains'], /* contains rice */
      fishFree: false, adultOnly: true, /* formulated for adult dogs (1 yr+) */
      tagline: 'Single-protein beef — rich flavor, easy on sensitive dogs.',
      benefits: ['One protein only — ideal for allergies', 'Rich, meaty taste picky eaters love', 'Iron-rich beef tripe & heart'],
      ingredients: 'Beef tripe, sweet potato, squash, rice + vitamins',
      kcalPerKg: 1252,
      nutrition: 'Single-protein beef.'
    },
    pork_rice: {
      key: 'pork_rice', label: 'Hearty Pork Rice',
      pricePerBagPhp: 259, allergens: ['pork', 'grains'], /* contains rice */
      fishFree: false, adultOnly: true, /* formulated for adult dogs (1 yr+) */
      tagline: 'Single-protein pork — light, tasty, easy to love.',
      benefits: ['One protein only — ideal for allergies', 'Mild flavor even fussy dogs finish', 'Taurine-rich pork heart'],
      ingredients: 'Pork, sweet potato, squash, rice + vitamins',
      kcalPerKg: 1645,
      nutrition: 'Light single-protein pork.'
    },
    surf_turf: {
      key: 'surf_turf', label: 'Surf & Turf',
      pricePerBagPhp: 259, allergens: ['beef', 'fish'],
      fishFree: false,
      tagline: 'Beef and fish for active, muscle-building dogs.',
      benefits: ['Omega-3 for joints & mobility', 'Lean beef protein', 'For dogs who love a richer bowl'],
      ingredients: 'Beef, sardine, squash, moringa, okra, canola oil, sweet potato',
      kcalPerKg: null,
      nutrition: 'Rich in omega-3.'
    },
    farmyard_feast: {
      key: 'farmyard_feast', label: 'Farmyard Feast',
      pricePerBagPhp: 259, allergens: ['chicken', 'pork', 'fish'],
      fishFree: false,
      tagline: 'A hearty farmyard medley.',
      benefits: ['Lean mix to support a healthy weight', 'Wholesome fibre', 'Full, satisfying bowl'],
      ingredients: 'Pork, sardine, chicken, squash, water spinach, carrots, broccoli',
      kcalPerKg: null,
      nutrition: 'Balanced lean proteins.'
    },
    coastal_blend: {
      key: 'coastal_blend', label: 'Coastal Blend',
      pricePerBagPhp: 259, allergens: ['fish', 'grains'], /* contains rice */
      fishFree: false,
      tagline: 'Fish-forward for skin, coat & sensitivities.',
      benefits: ['Omega-3 from fish for a shiny coat', 'Gentle on tummies', 'Limited, clean ingredient list'],
      ingredients: 'Sardine, tilapia, catfish, squash, rice, sweet potato, carrots, moringa',
      kcalPerKg: null,
      nutrition: 'Triple-fish omega-3 for skin & coat.'
    }
  };
  var RECIPE_ORDER = ['chicken_rice', 'beef_rice', 'pork_rice', 'surf_turf', 'coastal_blend', 'farmyard_feast'];
  var DEFAULT_RECIPE = 'chicken_rice';

  /* Variety-first recommendation pool. The default auto-select is four recipes;
     allergies and age can force fewer. One refused flavor must never mean
     "they refuse McDuffy". All safe recipes remain available at the same price. */
  var DEFAULT_RECIPE_MIX = ['chicken_rice', 'beef_rice', 'pork_rice', 'surf_turf'];
  var RECOMMENDED_RECIPE_COUNT = 4;

  /* Health/goal signal → preferred recipe (CONTEXT.md health-to-recipe map).
     Keys are the v2 schema ids for conditions, goals and weightGoal. The
     engine applies this preference THEN filters by allergen exclusion. */
  var HEALTH_RECIPE_MAP = {
    'food-sensitivities': 'chicken_rice',
    'pancreatitis': 'farmyard_feast',   /* leaner; lower-fat framing */
    'skin-coat': 'coastal_blend',
    'joints': 'surf_turf',
    'digestion': 'chicken_rice',
    'teeth-gums': 'farmyard_feast',
    'lose-lot': 'farmyard_feast',
    'lose-little': 'farmyard_feast'
  };

  /* --- Allergen catalogue (02-quiz-schema.json) --------------------------- */
  var ALLERGENS = [
    { value: 'beef', label: 'Beef' },
    { value: 'chicken', label: 'Chicken' },
    { value: 'dairy', label: 'Dairy' },
    { value: 'egg', label: 'Egg' },
    { value: 'fish', label: 'Fish' },
    { value: 'grains', label: 'Grains' },
    { value: 'lamb', label: 'Lamb' },
    { value: 'pork', label: 'Pork' },
    { value: 'soy', label: 'Soy' },
    { value: 'wheat', label: 'Wheat' }
  ];

  /* --- Activity slider tiers (02-quiz-schema.json activityTiers) ----------- */
  var ACTIVITY_TIERS = [
    { value: 'leisurely', label: 'Leisurely', detail: '15–30 minutes of walks a day', icon: 'activity-leisurely', calc: 'low' },
    { value: 'steady', label: 'Steady', detail: '30–60 minutes of walks a day', icon: 'activity-steady', calc: 'moderate' },
    { value: 'active', label: 'Active', detail: '1–2 hours of walks and play a day', icon: 'activity-active', calc: 'high' },
    { value: 'very-active', label: 'Very active', detail: '2+ hours a day, or a working dog', icon: 'activity-very-active', calc: 'high' }
  ];

  /* --- Weight goal (centered on Maintain) --------------------------------- */
  var WEIGHT_GOALS = [
    { value: 'lose-lot', label: 'Lose a lot of weight', condition: 'overweight' },
    { value: 'lose-little', label: 'Lose a little weight', condition: 'overweight' },
    { value: 'maintain', label: 'Maintain weight', condition: 'ideal' },
    { value: 'gain-little', label: 'Gain a little weight', condition: 'underweight' },
    { value: 'gain-lot', label: 'Gain a lot of weight', condition: 'underweight' }
  ];

  /* --- Health conditions + ingredients to leave out (multi-select, screen H1).
     The old separate "anything else you'd rather we leave out?" screen was
     merged here 2026-07-03 — allergies and plain preferences both flow through
     this one allergen grid (the engine excludes them identically). ---------- */
  var HEALTH_CONDITIONS = [
    { value: 'food-sensitivities', label: 'Allergies or ingredients to avoid', icon: 'condition-sensitivity', revealKey: 'allergens' },
    { value: 'pancreatitis', label: 'Pancreatitis', icon: 'condition-pancreas' },
    { value: 'none', label: 'None of these', icon: 'condition-none', exclusive: true }
  ];

  /* --- Health goals + expert-backed benefit bullets (screen H3) ------------
     `expert` is a key into EXPERTS. Bullets use {object}/{subject} tokens. */
  var HEALTH_GOALS = [
    { value: 'joints', label: 'Joints', icon: 'goal-joints', expert: 'vet',
      heading: 'How we support healthy joints:',
      bullets: ['Glucosamine support', 'Lean protein to keep {object} at a healthy weight', 'Omega-3 for comfortable mobility'] },
    { value: 'skin-coat', label: 'Skin & coat', icon: 'goal-skin-coat', expert: 'vet',
      heading: 'How we support a shiny coat:',
      bullets: ['Omega-3 from fish oil', 'Linseed for the skin barrier', 'Zinc for coat strength'] },
    { value: 'digestion', label: 'Digestion', icon: 'goal-digestion', expert: 'vet',
      heading: 'How we support easy digestion:',
      bullets: ['Gently cooked for tummies', 'Beet-pulp fibre', 'Balanced, high-quality protein'] },
    { value: 'teeth-gums', label: 'Teeth & gums', icon: 'goal-teeth', expert: 'vet',
      heading: 'How we support teeth & gums:',
      bullets: ['The right calcium-to-phosphorus balance', 'No added sugars', 'Wholesome, clean ingredients'] }
  ];

  /* --- Authority panels. We must NOT present a fabricated credentialed person
     to customers, so until a REAL named McDuffy vet/nutritionist + headshot
     exist these are non-personal team labels. TODO: when a real advisor is
     onboarded, set verified:true and use their real name/title/headshot. */
  /* Expert panels: until a REAL person is named, the view shows an honest
     factual marker (vet-formulated · AAFCO) instead of a placeholder face —
     a fake credential hurts trust more than no face. To ship a real expert,
     set `verified: true` + a real `headshot` URL + name/title; the panel then
     renders the photo + name automatically. Never invent a credentialed person. */
  var EXPERTS = {
    vet: { name: '', title: '', headshot: '', verified: false },
    nutritionist: { name: '', title: '', headshot: '', verified: false }
  };

  /* --- Current food (screen F1). answerLabel feeds {currentFoodLabel}.
     Values match the backend enum (dry|wet|mixed) exactly. ----------------- */
  var CURRENT_FOODS = [
    { value: 'dry', label: 'Dry kibble', icon: 'food-dry', answerLabel: 'dry kibble' },
    { value: 'wet', label: 'Wet food', icon: 'food-wet', answerLabel: 'wet food' },
    { value: 'mixed', label: 'Home-cooked or a mix', icon: 'food-mixed', answerLabel: 'food' }
  ];

  /* FLAVORS dataset removed 2026-07-03 with the flavors screen (it never steered
     the recipe recommendation). */

  /* --- Belief-priming yes-ladder (FD pattern, original copy) --------------- */
  var BELIEFS = [
    { value: 'want_best', label: 'want the very best for' },
    { value: 'curious', label: 'am curious about fresh food for' },
    { value: 'exploring', label: 'am just exploring options for' }
  ];

  /* --- PH delivery areas (early serviceability check). serviceable=false =>
     waitlist framing instead of a dead end.
     serviceable:true MUST match the checkout city dropdown (Metro Manila /
     Cavite / Laguna — mcduffy-subscription.liquid) or the customer hits a
     dead-end at delivery details after paying attention for 10 screens.
     Expanding coverage = flip the flag here AND add the optgroup there. --- */
  var PH_AREAS = [
    { value: 'metro-manila', label: 'Metro Manila (NCR)', serviceable: true },
    { value: 'cavite', label: 'Cavite', serviceable: true },
    { value: 'laguna', label: 'Laguna', serviceable: true },
    /* Owner-confirmed 2026-07-02 "we deliver to all of them": Rizal/Bulacan/
       Pampanga are serviceable — their cities are in the checkout dropdown
       (mcduffy-subscription.liquid optgroups). 'luzon-other' turned serviceable
       2026-07-14: the checkout now has an "Other Luzon city" option with
       free-text city + province inputs (#mds-city-other / #mds-province-other).
       Roll back = flip this flag to false (waitlist copy returns automatically). */
    { value: 'rizal', label: 'Rizal', serviceable: true },
    { value: 'bulacan', label: 'Bulacan', serviceable: true },
    { value: 'pampanga', label: 'Pampanga (Luzon)', serviceable: true },
    { value: 'luzon-other', label: 'Elsewhere in Luzon', serviceable: true },
    { value: 'visayas', label: 'Visayas', serviceable: false },
    { value: 'mindanao', label: 'Mindanao', serviceable: false },
    { value: 'other', label: 'Somewhere else', serviceable: false }
  ];

  /* --- PH breed list, size-annotated (drives the portion calc + weight
     estimate). size ∈ small|medium|large|giant. Aspin (asong Pinoy) first —
     a large share of PH dogs are local mixes; omitting them signals
     "not for my dog" (03-STRATEGY §4.4). estKg = midpoint estimate. -------- */
  var BREEDS = [
    { id: 'aspin', label: 'Aspin (Asong Pinoy)', size: 'medium', estKg: 14 },
    { id: 'aspin-small', label: 'Aspin — small build', size: 'small', estKg: 8 },
    { id: 'shih-tzu', label: 'Shih Tzu', size: 'small', estKg: 6 },
    { id: 'poodle-toy', label: 'Toy Poodle', size: 'small', estKg: 4 },
    { id: 'poodle-mini', label: 'Miniature Poodle', size: 'small', estKg: 7 },
    { id: 'poodle-standard', label: 'Standard Poodle', size: 'large', estKg: 25 },
    { id: 'pomeranian', label: 'Pomeranian', size: 'small', estKg: 3 },
    { id: 'chihuahua', label: 'Chihuahua', size: 'small', estKg: 3 },
    { id: 'maltese', label: 'Maltese', size: 'small', estKg: 3.5 },
    { id: 'yorkshire-terrier', label: 'Yorkshire Terrier', size: 'small', estKg: 3 },
    { id: 'dachshund', label: 'Dachshund', size: 'small', estKg: 8 },
    { id: 'pug', label: 'Pug', size: 'small', estKg: 8 },
    { id: 'jack-russell', label: 'Jack Russell Terrier', size: 'small', estKg: 7 },
    { id: 'beagle', label: 'Beagle', size: 'medium', estKg: 11 },
    { id: 'shih-poo', label: 'Shih-Poo', size: 'small', estKg: 6 },
    { id: 'cocker-spaniel', label: 'Cocker Spaniel', size: 'medium', estKg: 13 },
    { id: 'french-bulldog', label: 'French Bulldog', size: 'medium', estKg: 11 },
    { id: 'english-bulldog', label: 'English Bulldog', size: 'medium', estKg: 24 },
    { id: 'american-bully', label: 'American Bully', size: 'large', estKg: 32 },
    { id: 'pitbull', label: 'American Pit Bull Terrier', size: 'medium', estKg: 22 },
    { id: 'bulldog', label: 'Bulldog', size: 'medium', estKg: 23 },
    { id: 'japanese-spitz', label: 'Japanese Spitz', size: 'small', estKg: 7 },
    { id: 'bichon-frise', label: 'Bichon Frise', size: 'small', estKg: 5 },
    { id: 'mini-schnauzer', label: 'Miniature Schnauzer', size: 'small', estKg: 7 },
    { id: 'cavalier', label: 'Cavalier King Charles Spaniel', size: 'small', estKg: 8 },
    { id: 'chow-chow', label: 'Chow Chow', size: 'large', estKg: 25 },
    { id: 'basset-hound', label: 'Basset Hound', size: 'medium', estKg: 25 },
    { id: 'shiba-inu', label: 'Shiba Inu', size: 'medium', estKg: 10 },
    { id: 'corgi', label: 'Corgi', size: 'medium', estKg: 12 },
    { id: 'border-collie', label: 'Border Collie', size: 'medium', estKg: 18 },
    { id: 'australian-shepherd', label: 'Australian Shepherd', size: 'large', estKg: 25 },
    { id: 'labrador', label: 'Labrador Retriever', size: 'large', estKg: 30 },
    { id: 'golden-retriever', label: 'Golden Retriever', size: 'large', estKg: 30 },
    { id: 'german-shepherd', label: 'German Shepherd', size: 'large', estKg: 32 },
    { id: 'belgian-malinois', label: 'Belgian Malinois', size: 'large', estKg: 28 },
    { id: 'siberian-husky', label: 'Siberian Husky', size: 'large', estKg: 23 },
    { id: 'rottweiler', label: 'Rottweiler', size: 'large', estKg: 45 },
    { id: 'doberman', label: 'Doberman Pinscher', size: 'large', estKg: 38 },
    { id: 'dalmatian', label: 'Dalmatian', size: 'large', estKg: 27 },
    { id: 'boxer', label: 'Boxer', size: 'large', estKg: 30 },
    { id: 'great-dane', label: 'Great Dane', size: 'giant', estKg: 60 },
    { id: 'saint-bernard', label: 'Saint Bernard', size: 'giant', estKg: 75 },
    { id: 'mixed-small', label: 'Mixed — small', size: 'small', estKg: 8 },
    { id: 'mixed-medium', label: 'Mixed — medium', size: 'medium', estKg: 16 },
    { id: 'mixed-large', label: 'Mixed — large', size: 'large', estKg: 28 }
  ];

  /* Size fallback estimates (when breed is unknown) */
  var SIZE_EST_KG = { small: 7, medium: 16, large: 28, giant: 55 };

  /* BRANDS list removed 2026-07-03 with the currentBrand screen. */

  /* --- Misc copy strings (warm/premium McDuffy voice) --------------------- */
  var COPY = {
    introBadge: '🕐 About 3 minutes',
    introSub: 'Answer a few quick questions and we’ll craft a fresh, gently-cooked plan made just for your dog.',
    /* the quiz OUTPUTS (kept to the 3 things the quiz actually produces); the
       free tag is a product perk sold on the plan card, not listed here (2026-07-03) */
    introDeliverables: ['A right-sized daily portion', 'A recommended recipe', 'A clear daily ₱ price'],
    nameChip: 'Lovely name.',
    addDog: '+ Add another dog',
    confirm: 'Confirm',
    skip: 'Skip',
    estimateLink: 'Not sure? Use our estimate',
    crossContam: 'We cook a range of ingredients in one kitchen, so trace amounts can occur — for severe allergies, please work with your vet on the safest diet.',
    privacy: 'We’ll create your account and save {Name}’s plan so you can pick up where you left off.',
    riskReversal: 'No commitment — pause, skip, or cancel anytime.',
    freeTag: 'Free personalized dog tag with your first box.',
    tierLine: '40% off first-box food',
    tierLineReturning: 'Welcome back — member pricing' /* Batch C: shown to returning members instead of the intro */
  };

  /* Ad-campaign deep-link presets (ported from the live quiz for cutover
     message-match). A `?preset=<key>` on the landing URL swaps the welcome
     headline + subcopy so the quiz matches the ad the customer clicked.
     Keep keys in sync with the ad URLs; unknown presets are ignored. */
  var PRESETS = {
    picky:        { title: 'Build a fresh plan for a picky eater', sub: 'See recipes, portions, and your first-box pricing before you check out.' },
    delivery:     { title: 'Fresh dog food delivered around Metro Manila', sub: 'Build your dog’s plan, see the first box, and schedule delivery from your phone.' },
    personalized: { title: 'See your dog’s personalized McDuffy plan', sub: 'Recipe, portion, and pouch details based on your dog’s profile.' },
    sensitive:    { title: 'Build a gentle fresh-food plan', sub: 'Tell us about your dog and we’ll guide you to a recipe that fits — Chicken It Up is fish-free.' },
    price:        { title: 'Check your dog’s McDuffy plan before checkout', sub: 'See portions, recipes, and honest first-box + renewal pricing first. No payment until you’re ready.' },
    'shih-tzu':   { title: 'Build a fresh plan for your Shih Tzu', sub: 'Portions and recipes matched to small-dog appetites and picky mealtimes.' },
    ips2026: {
      title: 'Welcome to McDuffy at IPS 2026',
      sub: 'Build your dog’s fresh-food plan in about 90 seconds, see every price, then choose summit pickup or delivery.',
      tierLine: '40% off Box 1 food · 20% off Box 2 food',
      introBadge: 'Your IPS fast-track plan',
      introDeliverables: ['Portions matched to your dog', 'Exact Box 1, Box 2, and regular pricing', 'Summit pickup or delivery scheduling'],
      cta: 'Build the plan'
    }
  };

  return {
    PRESETS: PRESETS,
    BANNED_WORDS: BANNED_WORDS,
    RECIPES: RECIPES, RECIPE_ORDER: RECIPE_ORDER, DEFAULT_RECIPE: DEFAULT_RECIPE,
    DEFAULT_RECIPE_MIX: DEFAULT_RECIPE_MIX, RECOMMENDED_RECIPE_COUNT: RECOMMENDED_RECIPE_COUNT,
    HEALTH_RECIPE_MAP: HEALTH_RECIPE_MAP,
    ALLERGENS: ALLERGENS,
    ACTIVITY_TIERS: ACTIVITY_TIERS,
    WEIGHT_GOALS: WEIGHT_GOALS,
    HEALTH_CONDITIONS: HEALTH_CONDITIONS,
    HEALTH_GOALS: HEALTH_GOALS,
    EXPERTS: EXPERTS,
    CURRENT_FOODS: CURRENT_FOODS,
    BELIEFS: BELIEFS,
    PH_AREAS: PH_AREAS,
    BREEDS: BREEDS, SIZE_EST_KG: SIZE_EST_KG,
    COPY: COPY
  };
});
